<template>
  <f7-block-title>{{ $t('category.editContributionSettings') }}</f7-block-title>

  <f7-block>
    <f7-segmented round>
      <f7-button
        small round raised
        :active="mode === 'categories'"
        @click="switchMode('categories')"
        >{{ $t('category.byCategory') }}</f7-button
      >
      <f7-button
        small raised round
        :active="mode === 'free'"
        @click="switchMode('free')"
        >{{ $t('category.freeContributions') }}</f7-button
      >
    </f7-segmented>
  </f7-block>

  <template v-if="mode === 'categories'">
    <f7-list inset strong outline dividers media-list v-for="(row, i) in localAmounts" :key="i">
      <f7-list-item
        :title="$t('category.type')"
        smart-select
        :smart-select-params="{ openIn: 'sheet' }"
      >
        <select v-model="row.category">
          <option v-for="cat in CONTRIBUTION_CATEGORIES" :key="cat.value" :value="cat.value">
            {{ $t(cat.labelKey) }}
          </option>
        </select>
      </f7-list-item>
      <f7-list-input
        :label="$t('category.value')"
        v-if="row.category !== 'CUSTOM'"
        type="text"
        :placeholder="$t('category.valuePh')"
        :value="row.label"
        @input="row.label = $event.target.value"
      />
      <f7-list-input
        :label="$t('category.name')"
        v-if="row.category === 'CUSTOM'"
        type="text"
        :placeholder="$t('category.namePh')"
        :value="row.name"
        @input="row.name = $event.target.value"
      />
      <f7-list-input
        :label="$t('category.amountCfa')"
        type="number"
        :value="row.amount"
        @input="row.amount = $event.target.value"
      ><template #media>{{ row.currency }}</template>
    </f7-list-input>
      <f7-list-item :title="$t('common.remove')" link color="red" @click="localAmounts.splice(i, 1)" />
    </f7-list>
    <f7-block>
      <f7-button outline raised round small @click="localAmounts.push(newRow())"
        >+ {{ $t('category.addCategory') }}</f7-button
      >
    </f7-block>
    <f7-list inset strong>
      <f7-list-item
        checkbox
        :title="$t('category.lockAmounts')"
        :checked="lock"
        @checkbox:change="(v) => (lock = v)"
      />
    </f7-list>
  </template>

  <f7-list v-else inset strong>
    <f7-list-input
      :label="$t('category.freeContributionText')"
      type="textarea"
      :placeholder="$t('category.freeContributionTextPh')"
      :value="freeText"
      @input="freeText = $event.target.value"
    />
  </f7-list>

  <f7-block>
    <f7-button raised large round  fill @click="save">
      {{ $t('common.save') }}
    </f7-button>
  </f7-block>
</template>

<script setup>
import { ref } from 'vue'
import { f7 } from 'framework7-vue'
import { useCampaignsStore } from '../../js/pinia-stores/campaign.js'
import { CONTRIBUTION_CATEGORIES } from '../../constants/campaign-capabilities.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  campaignId: { type: String, required: true },
  categoryAmounts: { type: Array, default: () => [] },
  lockCategoryAmounts: { type: Boolean, default: true },
  freeContributionText: { type: String, default: '' },
})
const emit = defineEmits(['saved'])

const campaignsStore = useCampaignsStore()

const mode = ref(props.categoryAmounts.length ? 'categories' : 'free')
const localAmounts = ref(
  props.categoryAmounts
    .map(cloneRow)
    .filter(
      (r) =>
        r.category &&
        Number(r.amount) > 0 &&
        (r.category !== 'CUSTOM' || (r.name || '').trim().length > 0),
    ),
)
const lock = ref(props.lockCategoryAmounts)
const freeText = ref(props.freeContributionText)

function newRow() {
  return { category: CONTRIBUTION_CATEGORIES[0].value, name: '', label: '', amount: '' }
}

function cloneRow(r) {
  return { category: r.category, name: r.name ?? '', label: r.label ?? '', amount: r.amount ?? '' }
}

function switchMode(next) {
  mode.value = next
}

async function save() {
  const settings = {
    lockCategoryAmounts: lock.value,
    freeContributionText: freeText.value.trim(),
  }
  if (mode.value === 'categories') {
    settings.categoryAmounts = localAmounts.value
      .filter(
        (r) =>
          r.category &&
          Number(r.amount) > 0 &&
          (r.category !== 'CUSTOM' || (r.name || '').trim().length > 0),
      )
      .map((r) => ({
        category: r.category,
        name:
          r.category === 'CUSTOM' ? r.name?.trim() || null : null,
        label: r.label?.trim() || null,
        amount: Number(r.amount),
      }))
  }
  try {
    await campaignsStore.updateContributionSettings(props.campaignId, settings)
    emit('saved')
  } catch (e) {
    f7.dialog.alert(e?.response?.data?.message || e?.message || t('common.updateFailed'))
  }
}
</script>